import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TooltipDirective } from '@catch/directives';
import {
  ButtonComponent,
  EmptyPlaceholderComponent,
  ModalComponent,
  SkillLevelTagComponent,
  SkillLevelTagOption,
  TableSearchInputComponent,
} from '@catch/general-ui';
import { ConstantsService, LanguageService } from '@catch/services';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faPlus,
  faTrash,
  faLanguage,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { TableModule } from 'primeng/table';
import { LanguageLevelCode } from '../../../../../enums/language-level-code.enum';
import {
  PLATFORM_PROFILE_LANGUAGE_LEVELS,
  PlatformProfileLanguage,
} from '../../../../models/platform.models';

@Component({
  selector: 'app-platform-profile-languages',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    NgSelectModule,
    TableModule,
    ButtonComponent,
    EmptyPlaceholderComponent,
    ModalComponent,
    SkillLevelTagComponent,
    TableSearchInputComponent,
    FaIconComponent,
    TooltipDirective,
  ],
  templateUrl: './platform-profile-languages.component.html',
  styleUrl: './platform-profile-languages.component.scss',
})
export class PlatformProfileLanguagesComponent {
  private readonly constants = inject(ConstantsService);
  readonly languageService = inject(LanguageService);

  readonly form = input.required<FormGroup>();
  readonly duplicateLanguage = output<void>();

  readonly languageLevelOptions: SkillLevelTagOption[] =
    PLATFORM_PROFILE_LANGUAGE_LEVELS.map((level) => ({
      value: level,
      label: `ct-library.constants.language-level.${level}`,
    }));
  readonly newLanguageCode = new FormControl<string | null>(null);
  readonly newLanguageLevel = new FormControl<LanguageLevelCode>(
    LanguageLevelCode.B2,
    { nonNullable: true }
  );
  readonly icons = {
    plus: faPlus,
    trash: faTrash,
    empty: faLanguage,
    sort: faSort,
    sortUp: faSortUp,
    sortDown: faSortDown,
  };
  readonly languages = signal<PlatformProfileLanguage[]>([]);
  readonly searchQuery = signal<string | null>(null);
  readonly addModal = viewChild.required<ModalComponent>('addModal');

  readonly languageOptions = computed(() => {
    const selected = new Set(
      this.languages().map((language) => language.code.toLowerCase())
    );
    return this.constants.languageConstants.filter(
      (language) => !selected.has(language.code.toLowerCase())
    );
  });

  readonly visibleLanguages = computed(() => {
    const search = this.searchQuery()?.trim().toLowerCase() ?? '';
    if (!search) {
      return this.languages();
    }
    return this.languages().filter((language) => {
      const name = this.languageDisplayName(language.code).toLowerCase();
      return (
        language.code.toLowerCase().includes(search) ||
        name.includes(search) ||
        language.level.toLowerCase().includes(search)
      );
    });
  });

  constructor() {
    effect((onCleanup) => {
      const form = this.form();
      this.languages.set(
        (form.controls['languages'].value as PlatformProfileLanguage[]) ?? []
      );
      const sub = form.controls['languages'].valueChanges.subscribe((value) => {
        this.languages.set((value as PlatformProfileLanguage[]) ?? []);
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  /**
   * Track function for language table rows.
   * @param _index - Row index
   * @param language - Language row
   * @returns Stable row identity
   */
  trackByLanguage(_index: number, language: PlatformProfileLanguage): string {
    return language.code;
  }

  /**
   * Returns the display name for a language code.
   * @param code - ISO 639-1 code
   * @returns Localized language name
   */
  languageDisplayName(code: string): string {
    return this.constants.getLanguageName(this.constants.getLanguageByCode(code));
  }

  /**
   * Updates the free-text search for the languages table.
   * @param search - Search query or null
   */
  onSearch(search: string | null): void {
    this.searchQuery.set(search);
  }

  /**
   * Returns the translation key for a language level.
   * @param level - Language level value
   * @returns Translation key
   */
  languageLevelLabel(level: string): string {
    return `ct-library.constants.language-level.${level}`;
  }

  /**
   * Opens the add-language modal with a fresh form.
   */
  openAddModal(): void {
    this.newLanguageCode.setValue(null);
    this.newLanguageLevel.setValue(LanguageLevelCode.B2);
    this.addModal().open();
  }

  /**
   * Closes the add-language modal.
   */
  closeAddModal(): void {
    this.addModal().close();
  }

  /**
   * Adds the selected language from the modal to the profile.
   */
  submitAddLanguage(): void {
    const code = (this.newLanguageCode.value ?? '').toString().trim().toLowerCase();
    if (!code) {
      return;
    }
    if (this.hasLanguage(code)) {
      this.duplicateLanguage.emit();
      return;
    }
    this.patchLanguages([
      ...this.languages(),
      { code, level: this.newLanguageLevel.value },
    ]);
    this.closeAddModal();
  }

  /**
   * Updates the level of an existing language.
   * @param code - Language code
   * @param level - Selected level value
   */
  updateLanguageLevel(code: string, level: string): void {
    this.patchLanguages(
      this.languages().map((language) =>
        language.code === code
          ? { ...language, level: level as LanguageLevelCode }
          : language
      )
    );
  }

  /**
   * Removes a language from the profile.
   * @param code - Language code
   */
  removeLanguage(code: string): void {
    this.patchLanguages(
      this.languages().filter((language) => language.code !== code)
    );
  }

  /**
   * Checks whether a language code is already present.
   * @param code - Language code
   * @returns Whether the language exists
   */
  private hasLanguage(code: string): boolean {
    return this.languages().some(
      (language) => language.code.toLowerCase() === code.toLowerCase()
    );
  }

  /**
   * Writes the languages array into the parent form.
   * @param languages - Next languages
   */
  private patchLanguages(languages: PlatformProfileLanguage[]): void {
    this.form().controls['languages'].setValue(languages);
    this.form().controls['languages'].markAsDirty();
  }
}
